/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ModuleShell — orchestrator component.
 *
 * Reads the user's layout preference and module availability,
 * then renders the appropriate shell variant:
 * - SingleModuleSidebar (1 module — no switcher)
 * - DropdownShell / IconRailShell / TopBarShell (multi-module)
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLayoutStyle, useSidebarCollapsed, useLayoutStore } from '@/stores/layoutStore';
import { useUIStore } from '@/stores/uiStore';
import { useModuleNavigation } from '@/hooks/useModuleNavigation';
import { getModuleById, type ModuleDefinition } from '@/config/module-registry';
import { SingleModuleSidebar } from './SingleModuleSidebar';
import { DropdownShell } from './DropdownShell';
import { IconRailShell } from './IconRailShell';
import { TopBarShell } from './TopBarShell';
import { MobileBottomNav } from '../mobile-bottom-nav/MobileBottomNav';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import { navSectionsConfig } from '@/components/layouts/Sidebar';

/** Widths for each shell variant (must match the CSS in each component) */
const SHELL_WIDTHS = {
  single: '220px',
  dropdown: '220px',
  rail: '248px',
  topbar: '210px',
} as const;

/** Width of the icons-only rail when the shell is collapsed */
const COLLAPSED_SHELL_WIDTH = '60px';

interface ModuleShellOuterProps {
  /** Override active module id (e.g. 'admin' for superadmin layout) */
  moduleId?: string;
  children: React.ReactNode;
}

export function ModuleShell({ moduleId, children }: ModuleShellOuterProps) {
  const layoutStyle = useLayoutStyle();
  const collapsed = useSidebarCollapsed();
  const toggleCollapsed = useLayoutStore((s) => s.toggleSidebarCollapsed);
  const { t } = useTranslation('core');
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarOpen, closeSidebar, collapseBottomNav } = useUIStore();
  const { availableModules, activeModule: detectedModule, showSwitcher } = useModuleNavigation();

  // Allow override from parent (SuperAdminLayout passes moduleId='admin')
  const activeModule = moduleId
    ? getModuleById(moduleId) ?? detectedModule
    : detectedModule;

  // Resolve which width to set on :root so .app-main--with-sidebar can read it
  const resolvedStyle = !showSwitcher ? 'single' : layoutStyle;

  useEffect(() => {
    const width = collapsed
      ? COLLAPSED_SHELL_WIDTH
      : SHELL_WIDTHS[resolvedStyle] ?? SHELL_WIDTHS.single;
    document.documentElement.style.setProperty('--module-shell-width', width);
    // Drives the icons-only rules below, for every shell variant at once
    document.documentElement.classList.toggle('corex-shell-collapsed', collapsed);
    return () => {
      document.documentElement.style.removeProperty('--module-shell-width');
      document.documentElement.classList.remove('corex-shell-collapsed');
    };
  }, [resolvedStyle, collapsed]);

  // Close sidebar and bottom nav on route change (mobile)
  useEffect(() => {
    closeSidebar();
    collapseBottomNav();
  }, [location.pathname, closeSidebar, collapseBottomNav]);

  const handleModuleChange = (mod: ModuleDefinition) => {
    navigate(mod.basePath);
  };

  // No module matched (shouldn't happen in practice)
  if (!activeModule) return <>{children}</>;

  // Wrap shell output with mobile overlay + open class + bottom nav
  const wrapWithMobile = (shell: React.ReactNode) => (
    <>
      {isSidebarOpen && (
        <div className="ms-mobile-overlay" onClick={closeSidebar} />
      )}
      <div className={isSidebarOpen ? 'ms-mobile-open' : ''}>
        {shell}
      </div>
      <button
        type="button"
        className="ms-collapse-toggle"
        onClick={toggleCollapsed}
        aria-pressed={collapsed}
        title={collapsed
          ? t('core:sidebar.expand', 'Expand menu')
          : t('core:sidebar.collapse', 'Collapse menu')}
        aria-label={collapsed
          ? t('core:sidebar.expand', 'Expand menu')
          : t('core:sidebar.collapse', 'Collapse menu')}
      >
        {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
      </button>
      <MobileBottomNav />
      <GlobalSearch
        adminNavSections={activeModule?.isAdmin ? navSectionsConfig : undefined}
      />
      <style>{mobileStyles}</style>
    </>
  );

  // Single module — no switcher needed
  if (!showSwitcher) {
    return wrapWithMobile(
      <SingleModuleSidebar activeModule={activeModule}>
        {children}
      </SingleModuleSidebar>
    );
  }

  // Multi-module — pick shell by layout style
  const shellProps = {
    modules: availableModules,
    activeModule,
    onModuleChange: handleModuleChange,
    children,
  };

  let shell: React.ReactNode;
  switch (layoutStyle) {
    case 'rail':
      shell = <IconRailShell {...shellProps} />;
      break;
    case 'topbar':
      shell = <TopBarShell {...shellProps} />;
      break;
    case 'dropdown':
    default:
      shell = <DropdownShell {...shellProps} />;
      break;
  }

  return wrapWithMobile(shell);
}

const mobileStyles = `
  .ms-mobile-overlay {
    display: none;
  }

  /* ---- Collapse toggle (rides the sidebar's right edge) ---- */
  .ms-collapse-toggle {
    position: fixed;
    top: 74px;
    left: calc(var(--module-shell-width, 220px) - 11px);
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid var(--color-border-default);
    border-radius: 50%;
    background: var(--color-bg-secondary);
    color: var(--color-text-secondary);
    cursor: pointer;
    z-index: 101;
    box-shadow: var(--shadow-sm);
    transition: left 0.18s ease, color 0.15s ease, border-color 0.15s ease;
  }

  .ms-collapse-toggle:hover {
    color: var(--color-brand-primary);
    border-color: var(--color-brand-primary);
  }

  /* ---- Icons-only collapsed rail (desktop only) ---- */
  @media (min-width: 769px) {
    html.corex-shell-collapsed .ms-single-sidebar,
    html.corex-shell-collapsed .ms-dropdown-sidebar,
    html.corex-shell-collapsed .ms-rail-container,
    html.corex-shell-collapsed .ms-topbar-sidebar {
      width: ${COLLAPSED_SHELL_WIDTH};
      overflow-x: hidden;
    }

    /* Text drops out; icons stay. Class-suffix match so any module
       sidebar following the *-sidebar-* convention is covered. */
    html.corex-shell-collapsed [class*="sidebar-item-label"],
    html.corex-shell-collapsed [class*="sidebar-section-header"],
    html.corex-shell-collapsed [class*="sidebar-coming-soon"],
    html.corex-shell-collapsed [class*="sidebar-subtitle"],
    html.corex-shell-collapsed [class*="sidebar-title"],
    html.corex-shell-collapsed [class*="sidebar-user-info"],
    html.corex-shell-collapsed [class*="sidebar-badge"],
    html.corex-shell-collapsed .ms-single-title,
    html.corex-shell-collapsed .ms-single-subtitle,
    html.corex-shell-collapsed .ms-single-legal,
    html.corex-shell-collapsed .venta-search-kbd,
    html.corex-shell-collapsed .venta-search-trigger span {
      display: none !important;
    }

    /* Centre what remains */
    html.corex-shell-collapsed [class*="sidebar-item"],
    html.corex-shell-collapsed [class*="sidebar-brand"],
    html.corex-shell-collapsed .venta-search-trigger {
      justify-content: center !important;
      gap: 0 !important;
    }

    html.corex-shell-collapsed [class*="sidebar-nav"],
    html.corex-shell-collapsed .ms-single-header,
    html.corex-shell-collapsed .ms-single-nav {
      padding-left: 4px !important;
      padding-right: 4px !important;
    }
  }

  @media (max-width: 768px) {
    /* Mobile uses the drawer, not the collapsed rail */
    .ms-collapse-toggle {
      display: none;
    }

    .ms-mobile-overlay {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 99;
      animation: ms-overlay-fade 0.2s ease;
    }

    @keyframes ms-overlay-fade {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    /* Override translateX(-100%) on all shell variants when open */
    .ms-mobile-open .ms-single-sidebar,
    .ms-mobile-open .ms-dropdown-sidebar,
    .ms-mobile-open .ms-rail-container,
    .ms-mobile-open .ms-topbar-sidebar {
      transform: translateX(0) !important;
    }
  }
`;
